import React from 'react';
import { MapPin, MapPinModel, PinType } from '../models';
import BusinessDetailView from './BusinessDetailView';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Business } from '../models';
import { ChatService } from '../services/ChatService';
import { PinsService } from '../services/PinsService';
import { ChatMessage } from '../models/Chat';
import ChatBubble from './ChatBubble';
import EmojiReactionView from './EmojiReactionView';
import { useAuth } from '../context/AuthContext';

interface PinDetailsModalProps {
  pin: MapPin;
  isOpen: boolean;
  onClose: () => void;
}

const PinDetailsModal: React.FC<PinDetailsModalProps> = ({ pin, isOpen, onClose }) => {
  const [showBusinessDetail, setShowBusinessDetail] = React.useState(false);
  const [businessData, setBusinessData] = React.useState<Business | null>(null);

  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
  const [chatReactions, setChatReactions] = React.useState<Record<string, number>>({});
  const [userReactions, setUserReactions] = React.useState<string[]>([]);
  const [typingUsers, setTypingUsers] = React.useState<string[]>([]);
  const [newMessage, setNewMessage] = React.useState('');
  const [isConnected, setIsConnected] = React.useState(false);
  const [isLoadingChat, setIsLoadingChat] = React.useState(false);
  const { user, isAuthenticated } = useAuth();
  const currentUserId = user?.id ?? 'anonymous-user';
  const currentUserName = user?.displayName ?? 'Anonymous';
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);

  const chatServiceRef = React.useRef<ChatService | null>(null);
  const typingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  const isBusinessPin = pin.type === PinType.BUSINESS;

  React.useEffect(() => {
    if (isOpen && isBusinessPin) {
      loadBusinessData();
    }
  }, [isOpen, pin.id, pin.type]);

  React.useEffect(() => {
    if (isOpen && !isBusinessPin) {
      initializeChat();
    }

    return () => {
      if (chatServiceRef.current) {
        chatServiceRef.current.destroy();
        chatServiceRef.current = null;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setIsConnected(false);
      setChatMessages([]);
      setTypingUsers([]);
    };
  }, [isOpen, pin.id, pin.type]);

  // Auto-scroll to latest message like iOS ScrollViewReader
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages.length]);

  const initializeChat = async () => {
    if (isBusinessPin) return;

    setIsLoadingChat(true);
    try {
      chatServiceRef.current = new ChatService({
        onMessagesUpdate: setChatMessages,
        onReactionsUpdate: setChatReactions,
        onUserReactionsUpdate: setUserReactions,
        onTypingUsersUpdate: setTypingUsers,
        onError: (error: Error) => {
          console.error('Chat error:', error);
        }
      });

      chatServiceRef.current.startListening(pin.id, currentUserId);
      setIsConnected(true);
    } catch (error) {
      console.error('Error initializing chat:', error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const loadBusinessData = async () => {
    if (!isBusinessPin) return;

    try {
      const businessDoc = await getDoc(doc(db, 'businesses', pin.id));
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        setBusinessData({
          id: businessDoc.id,
          name: data.name || '',
          description: data.description || '',
          category: data.category || 'Services',
          location: data.location,
          imageUrl: data.imageUrl,
          phoneNumber: data.phoneNumber || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          createdBy: data.createdBy || '',
          isActive: data.isActive || false,
          subscriptionValidUntil: data.subscriptionValidUntil?.toDate() || new Date(),
          likes: data.likes || 0,
          userLikes: data.userLikes || [],
          rating: data.rating || 5.0
        });
      }
    } catch (error) {
      console.error('Error loading business data:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !chatServiceRef.current) return;

    try {
      await chatServiceRef.current.sendMessage(pin.id, newMessage.trim(), currentUserId, currentUserName);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Mirrors iOS: tapping an emoji sends an emoji chat message AND bumps the count
  const handleEmojiReaction = async (emoji: string) => {
    if (!isAuthenticated) {
      setActionMessage('Sign in to react.');
      return;
    }
    if (!chatServiceRef.current) return;

    try {
      await chatServiceRef.current.sendEmoji(pin.id, emoji, currentUserId, currentUserName);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const startTyping = async () => {
    if (!chatServiceRef.current) return;
    try {
      await chatServiceRef.current.startTyping(pin.id, currentUserId, currentUserName);
    } catch (error) {
      console.error('Error starting typing:', error);
    }
  };

  const stopTyping = async () => {
    if (!chatServiceRef.current) return;
    try {
      await chatServiceRef.current.stopTyping(pin.id, currentUserId);
    } catch (error) {
      console.error('Error stopping typing:', error);
    }
  };

  const handleVerify = async (isYes: boolean) => {
    if (!isAuthenticated) {
      setActionMessage('Sign in to verify pins.');
      return;
    }
    try {
      await PinsService.verifyPin(pin.id, isYes);
      setActionMessage('Thanks for verifying!');
    } catch (e) {
      console.error('Verify failed:', e);
      setActionMessage('Could not record your verification.');
    }
  };

  const handleReport = async () => {
    if (!isAuthenticated) {
      setActionMessage('Sign in to report pins.');
      return;
    }
    const reason = window.prompt('Why are you reporting this pin?', 'Inaccurate or outdated');
    if (reason === null) return;
    try {
      await PinsService.reportPin(pin.id, currentUserId, reason || 'Reported by user');
      setActionMessage('Report submitted. Thank you.');
    } catch (e) {
      console.error('Report failed:', e);
      setActionMessage('Could not submit your report.');
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (e.target.value.trim()) {
      startTyping();
      typingTimeoutRef.current = setTimeout(stopTyping, 1500);
    } else {
      stopTyping();
    }
  };

  const typingText = () => {
    switch (typingUsers.length) {
      case 0: return '';
      case 1: return `${typingUsers[0]} is typing…`;
      case 2: return `${typingUsers[0]} and ${typingUsers[1]} are typing…`;
      default: return 'Multiple people are typing…';
    }
  };

  const pinInfo = MapPinModel.getDisplayInfo(pin);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'white',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Hero image header — mirrors iOS PinDetailView: image .fit max 40%,
          top gradient, title + type capsule overlay, close button top-right */}
      <div style={{
        position: 'relative',
        flexShrink: 0,
        maxHeight: '40vh',
        minHeight: '160px',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {pin.imageURL ? (
          <img
            src={pin.imageURL}
            alt={pin.title}
            style={{ width: '100%', maxHeight: '40vh', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '200px',
            backgroundColor: '#8e8e93',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '40px', color: 'white'
          }}>
            {pinInfo.emoji}
          </div>
        )}

        {/* Top gradient for legibility */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '100px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          pointerEvents: 'none'
        }} />

        {/* Title + type capsule + close */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'flex-start', padding: '16px',
          paddingTop: 'calc(16px + env(safe-area-inset-top))'
        }}>
          <div>
            <div style={{
              color: 'white', fontSize: '18px', fontWeight: 700,
              textShadow: '0 1px 4px rgba(0,0,0,0.6)'
            }}>
              {pin.title}
            </div>
            <span style={{
              display: 'inline-block', marginTop: '6px',
              fontSize: '12px', fontWeight: 600, color: 'white',
              backgroundColor: 'rgba(0,122,255,0.85)',
              padding: '4px 10px', borderRadius: '999px'
            }}>
              {pinInfo.emoji} {pinInfo.displayName}
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: '32px', height: '32px', borderRadius: '50%',
              backgroundColor: 'rgba(60,60,60,0.7)', border: 'none',
              color: 'white', fontSize: '16px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Compact info strip: description + verify/report (web extras kept slim) */}
      {(pin.description || !isBusinessPin) && (
        <div style={{
          flexShrink: 0, padding: '10px 16px',
          borderBottom: '1px solid #e5e5ea', backgroundColor: 'white'
        }}>
          {pin.description && (
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#444', lineHeight: 1.4 }}>
              {pin.description}
            </p>
          )}
          {!isBusinessPin && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handleVerify(true)}
                style={{
                  padding: '6px 12px', borderRadius: '999px', cursor: 'pointer',
                  border: '1px solid #34c759', backgroundColor: 'transparent',
                  color: '#34c759', fontWeight: 600, fontSize: '12px'
                }}
              >
                ✓ Still there
              </button>
              <button
                onClick={() => handleVerify(false)}
                style={{
                  padding: '6px 12px', borderRadius: '999px', cursor: 'pointer',
                  border: '1px solid #ff9500', backgroundColor: 'transparent',
                  color: '#ff9500', fontWeight: 600, fontSize: '12px'
                }}
              >
                ✗ Not there
              </button>
              <button
                onClick={handleReport}
                style={{
                  padding: '6px 12px', borderRadius: '999px', cursor: 'pointer',
                  border: '1px solid #ff3b30', backgroundColor: 'transparent',
                  color: '#ff3b30', fontWeight: 600, fontSize: '12px'
                }}
              >
                🚩 Report
              </button>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: '11px', color: '#999' }}>
                {pin.verificationYes + pin.verificationNo > 0
                  ? `${Math.round((pin.verificationYes / (pin.verificationYes + pin.verificationNo)) * 100)}% verified`
                  : 'Unverified'}
              </span>
            </div>
          )}
          {actionMessage && (
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>{actionMessage}</p>
          )}
        </div>
      )}

      {/* Business pins open the business detail instead of chat */}
      {isBusinessPin ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}>
          <button
            onClick={() => setShowBusinessDetail(true)}
            style={{
              padding: '14px 32px', backgroundColor: '#007aff', color: 'white',
              border: 'none', borderRadius: '12px', cursor: 'pointer',
              fontSize: '16px', fontWeight: 600
            }}
          >
            View Business Details
          </button>
        </div>
      ) : (
        <>
          {/* Chat header — iOS LiveChatView: pin title + connection dot */}
          <div style={{
            height: '44px', flexShrink: 0, padding: '0 16px',
            display: 'flex', alignItems: 'center',
            borderBottom: '1px solid #e5e5ea'
          }}>
            <span style={{
              fontSize: '18px', fontWeight: 700,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            }}>
              {pin.title}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{
              width: '10px', height: '10px', borderRadius: '50%',
              backgroundColor: isConnected ? '#34c759' : '#ff3b30'
            }} />
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 4px', backgroundColor: 'white' }}>
            {typingUsers.length > 0 && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                margin: '0 8px 8px', padding: '4px 10px',
                backgroundColor: '#e9e9eb', borderRadius: '999px',
                fontSize: '12px', color: '#666'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  backgroundColor: '#34c759', animation: 'pulse 1.5s infinite'
                }} />
                {typingText()}
              </div>
            )}

            {isLoadingChat ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                Loading chat…
              </div>
            ) : chatMessages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '14px' }}>
                No messages yet. Be the first to comment!
              </div>
            ) : (
              chatMessages.map(message => (
                <ChatBubble key={message.id} message={message} currentUserId={currentUserId} />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Emoji reaction row — fixed set, matches iOS */}
          <EmojiReactionView
            reactions={chatReactions}
            onEmojiClick={handleEmojiReaction}
            userReactions={userReactions}
            canReact={isAuthenticated}
          />

          {/* Message input — sign-in required (Firestore rules deny anonymous writes) */}
          {!isAuthenticated ? (
            <p style={{
              textAlign: 'center', fontSize: '13px', color: '#888',
              margin: 0, padding: '12px',
              borderTop: '1px solid #e5e5ea',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom))'
            }}>
              Sign in to join the chat
            </p>
          ) : (
            <div style={{
              display: 'flex', gap: '8px', alignItems: 'center',
              padding: '8px 12px',
              paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
              borderTop: '1px solid #d1d1d6', flexShrink: 0
            }}>
              <input
                type="text"
                value={newMessage}
                onChange={handleMessageChange}
                placeholder="Add a comment..."
                style={{
                  flex: 1, padding: '10px 14px', border: 'none',
                  borderRadius: '20px', fontSize: '14px', outline: 'none',
                  backgroundColor: '#e9e9eb'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim()}
                aria-label="Send"
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  backgroundColor: '#007aff', border: 'none', color: 'white',
                  cursor: newMessage.trim() ? 'pointer' : 'default',
                  fontSize: '16px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  opacity: newMessage.trim() ? 1 : 0.5
                }}
              >
                ➤
              </button>
            </div>
          )}
        </>
      )}

      {/* Business Detail View */}
      {showBusinessDetail && isBusinessPin && businessData && (
        <BusinessDetailView
          businessId={businessData.id || pin.id}
          isOpen={showBusinessDetail}
          onClose={() => setShowBusinessDetail(false)}
        />
      )}
    </div>
  );
};

export default PinDetailsModal;
